import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, zip } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { State } from '@app/store/state/state';
import { getUIPackagesResultState } from '@packages/packages-result/store/selectors/ui.selector';
import { packagesResultRootSelector } from '@packages/packages-result/store/selectors/packages-result-root.selector';
import { HotelFilterApplier } from '@shared/hotel-itinerary/hotel-filters';
import {
  clearPackageResultsAction,
  searchPackageFlightsForDesktopAction,
  searchPackageFlightsForMobileAction,
  searchPackageHotelsWithFiltersAction,
  searchPackageHotelsWithMarkersAction,
} from '@packages/packages-result/store/actions/packages-result.actions';
import { HotelResultPackagesPayload } from '@packages/packages-result/entities/hotel-results';
import { HotelAmenity } from '@hotels/hotel-results/entities/hotel-filters.response';
import { PackageSeachResponse } from '@packages/packages-result/entities/packages-search';
import { AirResultPackagesPayload } from '@packages/packages-result/entities/air-results';
import { DEFAULT_PACKAGE_AIR_FILTERS } from '@packages/packages-result/constants/flight-filters.constants';
import { defaultPackageAirPagination } from '@packages/packages-result/mappers/flight-package-filters.mapper';
import { ZoneResponse } from '@app/entities/zones.response';
import { FilterSorterCriterias } from '@shared/filters/entities/fetch-filters.response';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { GlobalFacade } from '@app/global.facade';
import {
  AirFilterResponseVm, AirFilterVm, HotelFiltersResponse, HotelFilterVm
} from '@shared/magic-filter-bar/entities/filter-types';
import {
  DEFAULT_HOTEL_FILTERS, DEFAULT_HOTEL_PAGINATION, mapHotelFilterPayload
} from '@app/mappers/global-filters/hotel-global-filters.mapper';

@Injectable()
export class PackagesResultFiltersFacade {

  constructor(
    private readonly store: Store<State>,
    private readonly globalFacade: GlobalFacade,
  ) { }

  private hotelsPayload$: Observable<HotelResultPackagesPayload> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.hotelsPayload));

  private flightsPayload$: Observable<AirResultPackagesPayload> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.flightsPayload));

  private packageSearchData$: Observable<PackageSeachResponse> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.packageSearchData));

  private hotelTopZones$: Observable<ZoneResponse[]> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.packageHotelTopZones));

  public packageHotelAmenities$: Observable<HotelAmenity[]> = this.store.pipe(
    select(packagesResultRootSelector), map(state => state.packageHotelAmenities));

  public isLoadingHotelFilters$: Observable<boolean> = this.store.pipe(
    select(getUIPackagesResultState), map(state => state.isLoadingHotelFilters));

  public hotelFilters$: Observable<HotelFiltersResponse> = this.globalFacade.hotelFilters$;

  public packageHotelFilters$: Observable<HotelFilterVm[]> = this.globalFacade.hotelFilterVm$();

  public airFilters$: Observable<AirFilterResponseVm> = this.globalFacade.airFilters$;

  public packageAirFilters$: Observable<AirFilterVm[]> = this.globalFacade.airFilterVm$();

  public packageHotelTopZones$: Observable<ZoneResponse[]> = zip(this.hotelTopZones$, this.hotelsPayload$).pipe(
    map(([zoneList, payload]) => zoneList.filter(z => payload?.Zones?.indexOf(z.Name) === -1))
  );
  public airOrderCriteria$: Observable<SorterTypes> = this.flightsPayload$.pipe(
    map(payload => payload.OrderCriteria)
  );
  public airOrderCriteriaSectorNumber$: Observable<number> = this.flightsPayload$.pipe(
    map(payload => payload.OrderCriteriaSectorNumber)
  );
  public airOrderDesc$: Observable<boolean> = this.flightsPayload$.pipe(
    map(payload => payload.OrderDesc)
  );

  public sortFilterHasBeenChanged$: Observable<boolean> = this.globalFacade.ui.results.filters.sortingHasBeenChanged$;

  // DEPRECAR: Duplicado en Hotel Filters;
  public packageSortingHasBeenChanged(payload: boolean): void {
    this.globalFacade.ui.results.filters.sortingHasBeenChanged(payload);
  }

  public fetchHotelsWithFilters(filters: HotelFilterApplier, isMobile: boolean, block: boolean = false): void {
    this.clearPackageResults();
    this.hotelsPayload$.pipe(
      take(1),
      tap(payload => {
        const payloadWithFilter = {
          ...mapHotelFilterPayload(filters, {
            ...payload,
            ...DEFAULT_HOTEL_PAGINATION
          })
        } as HotelResultPackagesPayload;
        this.store.dispatch(searchPackageHotelsWithFiltersAction({ payload: payloadWithFilter, block }));
      })
    ).subscribe().unsubscribe();
  }

  public fetchHotelsWithSorter(sorter: FilterSorterCriterias): void {
    this.clearPackageResults();
    this.hotelsPayload$.pipe(take(1))
      .subscribe(payload => this.store.dispatch(
        searchPackageHotelsWithFiltersAction({
          payload: {
            ...payload,
            OrderCriteria: sorter.OrderCriteria,
            OrderDesc: sorter.OrderDesc,
            ...DEFAULT_HOTEL_PAGINATION
          }
        }))
      ).unsubscribe();
  }

  public fetchHotelsMarkersWithFilters(filters: HotelFilterApplier, block: boolean = false): void {
    zip(this.packageSearchData$.pipe(take(1)), this.hotelsPayload$.pipe(take(1))).pipe(
      map(([search, hotelsPayload]) => ({ search, hotelsPayload }))
    ).subscribe(({ search, hotelsPayload }) => {
      const payload = {
        ...mapHotelFilterPayload(filters, hotelsPayload), IncludeRooms: true,
        ResultCountLowerBound: 0, ResultCountUpperBound: search.SearchCountHotel
      } as HotelResultPackagesPayload;
      this.store.dispatch(searchPackageHotelsWithMarkersAction({ payload, block }));
    }).unsubscribe();
  }

  public clearPackageHotelFilters(isMobile: boolean): void {
    this.clearPackageResults();
    this.hotelsPayload$.pipe(take(1))
      .subscribe(payload => this.store.dispatch(searchPackageHotelsWithFiltersAction({
        payload: { ...payload, ...DEFAULT_HOTEL_PAGINATION, ...DEFAULT_HOTEL_FILTERS }
      })))
      .unsubscribe();
  }

  public clearPackageHotelFiltersInMap(): void {
    this.clearPackageResults();
    zip(this.packageSearchData$.pipe(take(1)), this.hotelsPayload$.pipe(take(1))).pipe(
      map(([search, hotelsPayload]) => ({ search, hotelsPayload }))
    ).subscribe(({ search, hotelsPayload }) => {
      this.store.dispatch(searchPackageHotelsWithMarkersAction({
        payload: {
          ...hotelsPayload, ...DEFAULT_HOTEL_FILTERS,
          ResultCountLowerBound: 0, ResultCountUpperBound: search.SearchCountHotel
        } as HotelResultPackagesPayload
      }));
    }).unsubscribe();
  }

  public getFirstHotelPageWithFilters(isMobile: boolean): void {
    this.clearPackageResults();
    this.hotelsPayload$.pipe(take(1))
      .subscribe(payload => this.store.dispatch(searchPackageHotelsWithFiltersAction({
        payload: { ...payload, ...DEFAULT_HOTEL_PAGINATION }
      })))
      .unsubscribe();
  }

  public clearPackageAirFilters(isMobile: boolean): void {
    this.clearPackageResults();
    this.flightsPayload$.pipe(take(1))
      .subscribe(payload => {
        if (isMobile) {
          this.store.dispatch(searchPackageFlightsForMobileAction({
            payload: { ...payload, ...defaultPackageAirPagination(isMobile), ...DEFAULT_PACKAGE_AIR_FILTERS },
            fetchFilters: false
          }));
        } else {
          this.store.dispatch(searchPackageFlightsForDesktopAction({
            payload: { ...payload, ...defaultPackageAirPagination(isMobile), ...DEFAULT_PACKAGE_AIR_FILTERS },
            fetchFilters: false
          }));
        }
      }).unsubscribe();
  }

  private clearPackageResults(): void {
    this.store.dispatch(clearPackageResultsAction());
  }
}
